import React, { useState, useReducer } from "react";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";

// Custom.
import PreviewLayout from "containers/Layout/PreviewLayout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_COLLEGE_CERTIFICATE,
  UPDATE_COLLEGE_CERTIFICATE,
  CREATE_COLLEGE_CERTIFICATE,
  SEARCH_GROUP,
} from "api";
import { VoAuth } from "@vocollege/app";
import { reducer } from "@vocollege/app/dist/modules/VoHelpers";
import { useStyles } from "ui/styles/form";
import CollegeCertificatePreview from "containers/Templates/CollegeCertificate/CollegeCertificatePreview";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { FormProps } from "@vocollege/components/dist/Form";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import { getGroupTitle, getGroupTitleValue } from "services/Utils";
import { EnhancedTableStatusCell } from "@vocollege/components";

const CertificateEdit: React.FC = () => {
  const urlParams = useParams<any>();
  const classes = useStyles();
  const { id } = urlParams;
  const [formLoading, setFormLoading] = useState(false);
  const [formState, setFormState] = useState({
    isDirty: false,
    isValid: false,
  });
  const navigate = useNavigate();

  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      from: "",
      to: "",
      college: null,
      author: null,
      done: 0,
    };
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());

  const handleCancel = () => {
    navigate("/college-certifikat");
  };

  const handleComplete = (obj: any) => {
    navigate(`/college-certifikat/${obj[Object.keys(obj)[0]].id}`);
  };

  const handleFormChange: FormProps["onFormChange"] = (formState) => {
    setFormState(formState);
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "from",
          label: I18n.get.collegeCertificate.labels.from,
          type: "date_field",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          onChange: (value: any) => dispatch({ key: "from", values: value }),
        },
        {
          name: "to",
          label: I18n.get.collegeCertificate.labels.to,
          type: "date_field",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          onChange: (value: any) => dispatch({ key: "to", values: value }),
        },

        {
          name: "college",
          label: I18n.get.misc.college,
          type: "entity_field",
          required: true,
          validation: {
            required: I18n.get.form.errors.required,
          },
          grid: {
            xs: 12,
          },
          params: {
            renderFieldValue: getGroupTitleValue,
            dialog: {
              types: ["group"],
              renderTitleField: getGroupTitle,
              extraDetails: (item: any) =>
                item?.status === 0 && (
                  <EnhancedTableStatusCell data={item.status} />
                ),
              query: SEARCH_GROUP,
              category: "searchGroup",
              variables: {
                categories: ["region", "local"],
              },
            },
          },
          onChange: (value: any) => dispatch({ key: "college", values: value }),
        },
        {
          name: "author",
          label: I18n.get.misc.author,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
        },
      ],
    });
    return tabs;
  };

  return (
    <PreviewLayout
      className={classes.root}
      leftColumn={
        <>
          <Alert severity="warning" className={classes.row}>
            {I18n.get.vocertMessages.correctPreview}
          </Alert>
          {!state.uid && !formLoading && (
            <Alert severity="error" className={classes.row}>
              {I18n.get.diploma.messages.saveBeforePrint}
            </Alert>
          )}
          <Form
            tabs={getTabs()}
            labels={{
              loading: I18n.get.form.labels.loadingContent,
              create:
                I18n.get.collegeCertificate.labels.createCollegeCertificate,
              created:
                I18n.get.collegeCertificate.messages.collegeCertificateSaved,
              updated:
                I18n.get.collegeCertificate.messages.collegeCertificateSaved,
              deleted:
                I18n.get.collegeCertificate.messages.collegeCertificateDeleted,
              fieldRequired: I18n.get.form.errors.required,
            }}
            operations={{
              category: "certCollegeCertificate",
              get: GET_COLLEGE_CERTIFICATE,
              create: CREATE_COLLEGE_CERTIFICATE,
              update: UPDATE_COLLEGE_CERTIFICATE,
            }}
            paths={{
              back: "/college-certifikat",
            }}
            initialState={getInitialState()}
            renderPageTitle={(state: any) => {
              if (state.id === 0) {
                return I18n.get.collegeCertificate.labels
                  .createCollegeCertificate;
              }
              return state.college.title;
            }}
            urlParams={urlParams}
            createParam="skapa"
            classes={{
              toolbar: classes.toolbar,
              paper: classes.form,
            }}
            onFormChange={handleFormChange}
            onQueryLoading={(loading) => setFormLoading(loading)}
            onDataChange={(data, fetchedData) => {
              dispatch({
                values: {
                  ...data,
                  uid: fetchedData.certCollegeCertificate.uid,
                },
              });
            }}
            onComplete={handleComplete}
            onCancel={handleCancel}
            loadQueryOnParamsChange
          />
        </>
      }
      rightColumn={
        !formLoading ? (
          <CollegeCertificatePreview
            item={state}
            formState={formState}
            id={id}
          />
        ) : (
          <></>
        )
      }
      rightColumnProps={{
        className: clsx(classes.previewWrapper),
      }}
    />
  );
};

export default CertificateEdit;
